import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismTooltip } from '@mylighthouse/prism-ember';
import { formatNumber, t } from 'ember-intl';
import { and } from 'ember-truth-helpers';
import {
  type Variance,
  VARIANCES,
} from 'lumen/utils/constants/budget-forecast-constants';

interface HotelBudgetForecastTableTdWithVarianceSignature {
  Args: {
    cellData?: {
      hasWarning?: boolean;
      inventoryDifference?: number;
      value: number | null;
      varianceType?: Variance;
      varianceValue: number | null;
    };
  };
  Element: HTMLDivElement;
}

export default class HotelBudgetForecastTableTdWithVariance extends Component<HotelBudgetForecastTableTdWithVarianceSignature> {
  @service declare intl: Services['intl'];

  get showVariance(): boolean {
    const varianceType = this.args.cellData?.varianceType;

    if (varianceType === undefined) {
      return false;
    }

    return varianceType !== VARIANCES.NONE;
  }

  get value(): string {
    const value = this.args.cellData?.value ?? undefined;

    if (value === undefined) {
      return this.intl.t('general.missing');
    }

    return this.intl.formatNumber(value, {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    });
  }

  get variance(): string {
    const varianceType = this.args.cellData?.varianceType;
    const varianceValue = this.args.cellData?.varianceValue ?? undefined;

    let sign = '';
    let value = this.intl.t('general.missing');
    let unit = '';

    if (varianceType === VARIANCES.PERCENTAGE) {
      unit = '%';
    }

    if (varianceValue === undefined) {
      return `${sign}${value}${unit}`;
    }

    if (varianceValue > 0) {
      sign = '+';
    }

    value = this.intl.formatNumber(varianceValue, {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    });

    return `${sign}${value}${unit}`;
  }

  get varianceClass(): string {
    const varianceValue = this.args.cellData?.varianceValue ?? undefined;

    if (varianceValue === undefined) {
      return '';
    }

    return varianceValue > 0 ? 'text-success-default' : 'text-critical-default';
  }

  <template>
    <div
      class="text-200--regular flex flex-col tabular-nums
        {{if this.showVariance 'py-200'}}
        {{if @cellData.hasWarning 'text-warning-default'}}"
      ...attributes
    >
      <div class="truncate" title={{this.value}}>
        {{this.value}}

        {{#if (and @cellData.hasWarning @cellData.inventoryDifference)}}
          <PrismTooltip
            @side="top"
            @text={{t
              "settings.budget_forecast.inventory_excl_ooo.warning_message.count"
              count=(formatNumber
                @cellData.inventoryDifference
                maximumFractionDigits=0
                minimumFractionDigits=0
              )
            }}
          />
        {{/if}}
      </div>

      {{#if this.showVariance}}
        <div class="truncate {{this.varianceClass}}" title={{this.variance}}>
          {{this.variance}}
        </div>
      {{/if}}
    </div>
  </template>
}
